import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { InteractionStatus } from '@azure/msal-browser'
import { useAuth } from '../hooks/useAuth'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Mtds } from 'prove-common'
import { ErrorMessage } from '../components/error-message/ErrorMessage'

type LoginScreenProps = {
  logo: React.ReactNode
  loginButtonText: string
  homeUrl: string
}

export const LoginScreen = ({
  logo,
  loginButtonText,
  homeUrl
}: LoginScreenProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, loginRequest } = useAuth()
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const shouldResetAuth =
    new URLSearchParams(location.search).get('logout') === 'true'
  const [resetStatus, setResetStatus] = useState<'idle' | 'pending' | 'failed'>(
    'idle'
  )
  const [loginError, setLoginError] = useState<Error>()
  const isLoading =
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect ||
    isAuthenticated ||
    resetStatus === 'pending'

  useEffect(() => {
    if (
      shouldResetAuth &&
      resetStatus === 'idle' &&
      inProgress === InteractionStatus.None
    ) {
      setResetStatus('pending')
      void logout().catch((error) => {
        setResetStatus('failed')
        setLoginError(
          error instanceof Error
            ? error
            : new Error('Kunne ikke nullstille innloggingen.')
        )
        navigate('/login', { replace: true })
      })
      return
    }

    if (isAuthenticated) {
      navigate(homeUrl, { replace: true })
    }
  }, [
    homeUrl,
    inProgress,
    isAuthenticated,
    navigate,
    logout,
    resetStatus,
    shouldResetAuth
  ])

  const handleLogin = async () => {
    setLoginError(undefined)
    try {
      await instance.loginRedirect(loginRequest)
    } catch (error) {
      setLoginError(
        error instanceof Error
          ? error
          : new Error('Kunne ikke starte innloggingen.')
      )
    }
  }

  return (
    <Mtds.Grid className='h-screen' data-align='center' data-justify='center'>
      <Mtds.Card
        as={Mtds.Grid}
        data-justify='center'
        data-gap={12}
        className='p-15'
      >
        {logo}
        {loginError && <ErrorMessage error={loginError} />}
        <Mtds.Button
          data-variant='primary'
          onClick={handleLogin}
          aria-busy={isLoading}
          disabled={isLoading}
        >
          {loginButtonText}
        </Mtds.Button>
      </Mtds.Card>
    </Mtds.Grid>
  )
}
